import { useCallback, useEffect, useMemo, useState } from 'react';
import { printClient } from '../utils/printClient';
import {
  readStoredPrinter,
  resolvePrinterSelection,
  writeStoredPrinter,
} from '../utils/printerState.mjs';

export default function usePrinter({ enabled = true, pollMs = 30000 } = {}) {
  const [printers, setPrinters] = useState([]);
  const [stored, setStored] = useState(() => readStoredPrinter());
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) {
      setPrinters([]);
      setLoading(false);
      return [];
    }
    if (!silent) setLoading(true);
    try {
      const items = await printClient.listPrinters({ silent: true });
      setPrinters(items);
      setError('');
      return items;
    } catch (requestError) {
      setPrinters([]);
      setError(requestError?.response?.data?.message || 'Не удалось получить список принтеров');
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
    if (!enabled || !pollMs) return undefined;
    const timer = setInterval(() => refresh({ silent: true }), pollMs);
    return () => clearInterval(timer);
  }, [enabled, pollMs, refresh]);

  const selection = useMemo(
    () => resolvePrinterSelection(printers, stored),
    [printers, stored],
  );

  const selectPrinter = useCallback((uid) => {
    const printer = printers.find((item) => item.printer_uid === uid);
    if (!printer) return;
    setStored(writeStoredPrinter({
      printer_uid: printer.printer_uid,
      label: printer.name,
    }));
  }, [printers]);

  const clearPrinter = useCallback(() => {
    writeStoredPrinter(null);
    setStored(null);
  }, []);

  return {
    printers,
    selectedPrinter: selection.selected,
    storedPrinter: stored,
    unavailable: selection.unavailable,
    loading,
    error,
    canPrint: Boolean(selection.selected) && !loading,
    selectPrinter,
    clearPrinter,
    refresh,
  };
}
